import {
  Box,
  HStack,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { format, isSameDay, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { translate } from '../../../i18n';
import { HourlyForecastDto } from '../../../models/weather/dto/weather-forecast.dto';
import { useGetWeatherForecast } from '../hooks/useGetWeatherForecast';
import { getConditionEmoji, hasRainRisk } from '../utils/weather.utils';

type WeatherStripProps = {
  locationId?: string;
  date: Date;
  hasOutdoorCourts: boolean;
};

export default function WeatherStrip({
  locationId,
  date,
  hasOutdoorCourts,
}: WeatherStripProps) {
  const { data: forecast } = useGetWeatherForecast(locationId);

  const hoursForDate = useMemo(() => {
    if (!forecast?.hourly?.length) return [];
    return forecast.hourly.filter((h) => isSameDay(parseISO(h.datetime), date));
  }, [forecast, date]);

  const stripBg = useColorModeValue('gray.50', 'gray.700');
  const stripBorderColor = useColorModeValue('gray.200', 'gray.600');

  if (!hoursForDate.length || !hasOutdoorCourts) return null;

  return (
    <Box
      bg={stripBg}
      borderRadius="md"
      border="1px solid"
      borderColor={stripBorderColor}
      px={3}
      py={2}
      mb={3}
      overflowX="auto"
    >
      <HStack spacing={0} minW="fit-content">
        {hoursForDate.map((h) => (
          <WeatherHourCell key={h.datetime} hour={h} />
        ))}
      </HStack>
    </Box>
  );
}

function WeatherHourCell({ hour }: { hour: HourlyForecastDto }) {
  const time = format(parseISO(hour.datetime), 'HH:mm');
  const emoji = getConditionEmoji(hour.condition);
  const isRainRisk = hasRainRisk(hour.precipitationProbability);

  const rainBg = useColorModeValue('blue.50', 'blue.900');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');
  const timeColor = useColorModeValue('gray.500', 'gray.400');
  const rainTextColor = useColorModeValue('brand.600', 'brand.300');

  return (
    <Tooltip
      label={`${hour.temperature}°C (${translate('weather.feels_like')} ${hour.feelsLike}°C) | ${translate('weather.wind')}: ${hour.windSpeedKmh} km/h | ${translate('weather.humidity')}: ${hour.humidity}% | ${translate('weather.rain')}: ${hour.precipitationProbability}%`}
      fontSize="xs"
    >
      <VStack
        spacing={0}
        minW="48px"
        px={1}
        py={1}
        borderRadius="sm"
        bg={isRainRisk ? rainBg : 'transparent'}
        cursor="default"
        _hover={{ bg: hoverBg }}
      >
        <Text fontSize="2xs" color={timeColor}>
          {time}
        </Text>
        <Text fontSize="md">{emoji}</Text>
        <Text fontSize="xs" fontWeight="semibold">
          {Math.round(hour.temperature)}°
        </Text>
        {isRainRisk && (
          <Text fontSize="2xs" color={rainTextColor} fontWeight="bold">
            {hour.precipitationProbability}%
          </Text>
        )}
      </VStack>
    </Tooltip>
  );
}
